import Konva from 'konva'
import { RGBColor, color } from 'd3-color'
import typedStore from '@/store/typedStore'
import { OrbVizAnalytics } from '@/plugins/analytics'
import { VISUALISATION_VIEWBOX_POSITION_OFFSET, VISUALISATION_VIEWBOX_SIZE, defaultLevelBasedPositions } from '@/constants'
import { BubblesPopupDetails, ComparisonEqualityOperators, Coordinate2D, CustomBubblesCompareCategory, CustomBubblesFilter, LevelBasedPositions, VisNode } from '@/types'
import { RenderedBubble, RenderedBubbleGroup, RenderedBubbleHalo, RenderedBubblePulse } from '@/components/public/Bubbles/types'
import Bubble from '@/components/public/Bubbles/Bubble'

const FADE_DURATION_SECONDS = 0.2

export function getBubblesCanvas (): HTMLElement | null {
  return document.querySelector('#bubblesCanvas')
}

export function buildBubbleCircle (bubble: RenderedBubble, node: VisNode, analytics: OrbVizAnalytics): Konva.Circle {
  const circle = new Konva.Circle({
    id: bubble.path.toString(),
    name: 'bubble',
  })

  circle.on('mouseover', function () {
    const canvas = getBubblesCanvas()
    if (canvas) {
      canvas.style.cursor = 'pointer'
    }

    bubble.isHighlighted = true
    updateBubbleCircleProperties(this, bubble, bubblesVisualisationScaleFactor())

    typedStore.activeVisualisation.setActiveBubblesPopupDetails({
      node,
      position: transformCoordinatesBubblesToScreen(bubble.currentPosition, bubblesVisualisationScaleFactor(), bubblesMinSizeSquareOffset()),
      radius: (bubble.radius + bubble.halo.totalWidth) * bubblesVisualisationScaleFactor(),
    } as BubblesPopupDetails)
  })

  circle.on('mouseout', function () {
    const canvas = getBubblesCanvas()
    if (canvas) {
      canvas.style.cursor = 'default'
    }

    bubble.isHighlighted = false
    updateBubbleCircleProperties(this, bubble, bubblesVisualisationScaleFactor())

    typedStore.activeVisualisation.setActiveBubblesPopupDetails(undefined)
  })

  circle.on('click', function () {
    typedStore.activeVisualisation.updateStoryPath(bubble.path)

    analytics.event('click', {
      event_category: 'bubbles',
      event_label: bubble.path,
    })
  })

  return circle
}

export function buildBubbleHaloCircle (bubble: RenderedBubble): Konva.Circle {
  return new Konva.Circle({
    id: bubble.path.toString(),
    name: 'halo',
  })
}

export function buildBubblePulseCircle (bubble: RenderedBubble): Konva.Circle {
  return new Konva.Circle({
    id: bubble.path.toString(),
    name: 'pulse',
  })
}

function buildBubblePulseAnimation (pulse: RenderedBubblePulse, circle: Konva.Circle): Konva.Animation {
  const pulseDuration = pulse.duration * 1000
  const pulseStrokeWidth = pulse.strokeWidth * bubblesVisualisationScaleFactor()
  const pulseStrokeOpacity = pulse.strokeOpacity
  return new Konva.Animation((frame) => {
    const normalizedTime = ((frame!.time % pulseDuration) / pulseDuration) // Normalized time [0, 1]

    let strokeWidth
    let strokeOpacity

    // Dividing animation into phases according to the CSS keyframes
    if (normalizedTime < 0.25) {
      strokeWidth = 0
      strokeOpacity = interpolateBetweenValues(0, pulseStrokeOpacity, normalizedTime / 0.25)
    } else if (normalizedTime < 0.5) {
      strokeWidth = interpolateBetweenValues(0, pulseStrokeWidth, (normalizedTime - 0.25) / 0.25)
      strokeOpacity = interpolateBetweenValues(pulseStrokeOpacity, 0, (normalizedTime - 0.25) / 0.25)
    } else {
      strokeWidth = interpolateBetweenValues(pulseStrokeWidth, 0, (normalizedTime - 0.5) / 0.5)
      strokeOpacity = 0
    }

    circle.strokeWidth(strokeWidth)
    const stroke = color(pulse.strokeColor) as RGBColor
    stroke.opacity = strokeOpacity

    circle.stroke(stroke.formatRgb())
  }, circle.getLayer())
}

function interpolateBetweenValues (startValue: number, endValue: number, interpolationFactor: number): number {
  return (1 - interpolationFactor) * startValue + interpolationFactor * endValue
}

export function fadeInBubbleGroup (group: RenderedBubbleGroup): void {
  fadeInCircle(group.bubbleCircle)
  fadeInCircle(group.haloCircle)
  fadeInCircle(group.pulseCircle)
}

function fadeInCircle (circle: Konva.Circle): void {
  // We cannot easily grow the circle in the same way as we shrink the circle
  // while fading out as the circle radius can change while the animation is
  // running and this change is not reflected when the animation completes.
  circle.opacity(0)
  circle.to({
    opacity: 1,
    easing: Konva.Easings.EaseInOut,
    duration: FADE_DURATION_SECONDS,
  })
}

export function fadeOutBubbleGroup (group: RenderedBubbleGroup): void {
  fadeOutCircle(group.bubbleCircle)
  fadeOutCircle(group.haloCircle)
  fadeOutCircle(group.pulseCircle)
}

function fadeOutCircle (circle: Konva.Circle): void {
  circle.to({
    opacity: 0,
    radius: 0,
    easing: Konva.Easings.EaseInOut,
    duration: FADE_DURATION_SECONDS,
    onFinish: () => {
      circle.destroy()
    },
  })
}

export function updateBubbleGroupProperties (group: RenderedBubbleGroup, pulseAnimations: Record<string, Konva.Animation> = {}, bubblesVisualisationScaleFactor: number) {
  const bubble = group.bubble
  updateBubbleCircleProperties(group.bubbleCircle, bubble, bubblesVisualisationScaleFactor)
  updateBubbleHaloCircleProperties(group.haloCircle, bubble.halo, bubblesVisualisationScaleFactor)
  updateBubblePulseCircleProperties(group.pulseCircle, bubble.pulse, pulseAnimations, bubblesVisualisationScaleFactor)
}

function updateBubbleCircleProperties (circle: Konva.Circle, bubble: RenderedBubble, bubblesVisualisationScaleFactor: number) {
  // The rendered radius needs to match the radius used by the simulation.
  // Because half of the circle's stroke is rendered inside the circle and
  // the other half is rendered outside the circle we need to accommodate this
  // and remove half of the stroke (borderWidth) from the circle radius value.
  const circleStrokeWidth = bubble.strokeWidth * bubblesVisualisationScaleFactor
  const circleRadius = (bubble.radius * bubblesVisualisationScaleFactor) - (circleStrokeWidth / 2)

  circle.radius(circleRadius)
  circle.strokeWidth(circleStrokeWidth)
  circle.stroke(bubble.strokeColor)
  circle.fill(bubble.fill)
}

function updateBubbleHaloCircleProperties (circle: Konva.Circle, halo: RenderedBubbleHalo, bubblesVisualisationScaleFactor: number) {
  if (halo.show) {
    const circleStrokeWidth = halo.strokeWidth * bubblesVisualisationScaleFactor
    const circleRadius = halo.radius * bubblesVisualisationScaleFactor

    circle.visible(true)
    circle.radius(circleRadius)
    circle.strokeWidth(circleStrokeWidth)
    circle.stroke(halo.strokeColor)
  } else {
    circle.visible(false)
  }
}

function updateBubblePulseCircleProperties (circle: Konva.Circle, pulse: RenderedBubblePulse, pulseAnimations: Record<string, Konva.Animation> = {}, bubblesVisualisationScaleFactor: number) {
  // stop the existing animation if one already exists as we will
  // create a new one with any new pulse properties
  const animation = pulseAnimations[circle.id()]
  if (animation) {
    animation.stop()
    delete pulseAnimations[circle.id()]
  }

  if (pulse.show) {
    const circleRadius = pulse.radius * bubblesVisualisationScaleFactor

    circle.visible(true)
    circle.radius(circleRadius)

    const newAnimation = buildBubblePulseAnimation(pulse, circle)
    pulseAnimations[circle.id()] = newAnimation
    newAnimation.start()
  } else {
    circle.visible(false)
  }
}

export function shouldBubblesAmalgamate (bubbles: Bubble[]) {
  // If a bubble is within 5% of its destination then it is considered near its destination
  const nearDestinationThreshold = 0.10
  let count = 0

  for (const bubble of bubbles) {
    const distanceX = bubble.destinationPosition.x - bubble.currentPosition.x
    const distanceY = bubble.destinationPosition.y - bubble.currentPosition.y
    const distanceTotal = Math.sqrt(distanceX ** 2 + distanceY ** 2)
    const diagonalDistance = Math.sqrt(VISUALISATION_VIEWBOX_SIZE ** 2 + VISUALISATION_VIEWBOX_SIZE ** 2)
    const distancePercentage = diagonalDistance === 0 ? 0 : distanceTotal / diagonalDistance

    if (distancePercentage <= nearDestinationThreshold) {
      count++
    }
  }

  // The bubbles should amalgamate if 20% or more of them are near their destination
  return count / bubbles.length >= 0.2
}

function bubblesVisualisationScaleFactor () {
  return typedStore.public.size.bubblesVisualisationScaleFactor
}

function bubblesMinSizeSquareOffset () {
  return typedStore.public.size.bubblesMinSizeSquareOffset
}

export function getLevelBasedPosition (path: string, positionsMap: Record<string, LevelBasedPositions>, level: number): Coordinate2D {
  const positions = positionsMap[path] ?? defaultLevelBasedPositions()

  if (positions.levelOverrides && positions.levelOverrides[level]) {
    return positions.levelOverrides[level]
  }
  return positions.default
}

// We allow visualisationScaleFactor and minSizeSquareOffset to be passed in so cached values can be provided instead
// of accessing the store directly.
export function transformCoordinatesBubblesToScreen (coordinates: Coordinate2D, bubblesVisualisationScaleFactor: number, bubblesMinSizeSquareOffset: Coordinate2D) {
  return {
    x: transformCoordinateBubblesToScreen(coordinates.x, bubblesVisualisationScaleFactor, bubblesMinSizeSquareOffset.x),
    y: transformCoordinateBubblesToScreen(coordinates.y, bubblesVisualisationScaleFactor, bubblesMinSizeSquareOffset.y),
  }
}

function transformCoordinateBubblesToScreen (coordinate: number, bubblesVisualisationScaleFactor: number, minSizeSquareOffset: number) {
  return (coordinate - VISUALISATION_VIEWBOX_POSITION_OFFSET) * bubblesVisualisationScaleFactor + minSizeSquareOffset
}

export function transformCoordinatesScreenToBubbles (coordinates: Coordinate2D) {
  return {
    x: transformCoordinateScreenToBubbles(coordinates.x, bubblesMinSizeSquareOffset().x),
    y: transformCoordinateScreenToBubbles(coordinates.y, bubblesMinSizeSquareOffset().y),
  }
}

function transformCoordinateScreenToBubbles (coordinate: number, minSizeSquareOffset: number) {
  return (coordinate - minSizeSquareOffset) / bubblesVisualisationScaleFactor() + VISUALISATION_VIEWBOX_POSITION_OFFSET
}

export function checkHaloBubblesFilter (filter: CustomBubblesFilter, visNode: VisNode) {
  const { category, compare, operator, value } = filter
  const categoryData = visNode.data[category]
  const compareTo = compare === CustomBubblesCompareCategory ? visNode.data[value] : value
  switch (operator) {
    case ComparisonEqualityOperators.LESS_THAN:
      return Number(categoryData) < Number(compareTo)
    case ComparisonEqualityOperators.GREATER_THAN:
      return Number(categoryData) > Number(compareTo)
    case ComparisonEqualityOperators.LESS_THAN_OR_EQUAL:
      return Number(categoryData) <= Number(compareTo)
    case ComparisonEqualityOperators.GREATER_THAN_OR_EQUAL:
      return Number(categoryData) >= Number(compareTo)
    case ComparisonEqualityOperators.STRICT_EQUAL:
      if (isNaN(Number(categoryData)) && isNaN(Number(compareTo))) return categoryData === compareTo
      else return Number(categoryData) === Number(compareTo)
    case ComparisonEqualityOperators.STRICT_UNEQUAL:
      if (isNaN(Number(categoryData)) && isNaN(Number(compareTo))) return categoryData !== compareTo
      else return Number(categoryData) !== Number(compareTo)
    default:
      return false
  }
}
